import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { X, Loader2 } from 'lucide-react';

interface GenerateDatasetMappingModalProps {
  datasetId: string;
  datasetName: string;
  bankMappings: Record<string, string>;
  onClose: () => void;
}

import { Merchant, Bank } from '../../types';

export function GenerateDatasetMappingModal({ datasetId, datasetName, bankMappings, onClose }: GenerateDatasetMappingModalProps) {
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [selectedMerchant, setSelectedMerchant] = useState<string>('');
  const [bankMap, setBankMap] = useState<Record<string, string>>(bankMappings);
  const [loading, setLoading] = useState(true);
  const [mappingName, setMappingName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [merchantsResult, banksResult] = await Promise.all([
        supabase.from('merchants').select('*').order('name'),
        supabase.from('banks').select('*').order('name')
      ]);

      if (merchantsResult.error) throw merchantsResult.error;
      if (banksResult.error) throw banksResult.error;

      setMerchants(merchantsResult.data || []);
      setBanks(banksResult.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMerchant) {
      setError('Please select a merchant');
      return;
    }

    if (!mappingName.trim()) {
      setError('Please enter a mapping name');
      return;
    }

    // Validate all banks are mapped
    const unmappedBanks = Object.entries(bankMap)
      .filter(([bank, value]) => !value && bank.trim().length > 0)
      .map(([bank]) => bank);

    if (unmappedBanks.length > 0) {
      setError(`Please map all banks: ${unmappedBanks.join(', ')}`);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Create mapping
      const { data: mappingId, error: mappingError } = await supabase
        .rpc('create_dataset_mapping', {
          name_param: mappingName.trim(),
          dataset_id_param: datasetId,
          merchant_id_param: selectedMerchant,
          bank_mappings: bankMap
        });

      if (mappingError) throw mappingError;

      // Navigate to the dataset mappings
      navigate('/datasets/mappings');
    } catch (err) {
      console.error('Error creating mapping:', err);
      setError(err instanceof Error ? err.message : 'Failed to create mapping');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Map Dataset</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Mapping Name */}
            <div>
              <label htmlFor="mappingName" className="block text-sm font-medium text-gray-700">
                Mapping Name
              </label>
              <input
                type="text"
                id="mappingName"
                value={mappingName}
                onChange={(e) => setMappingName(e.target.value)}
                placeholder="Enter a name for this mapping"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              />
            </div>

            {/* Merchant Selection */}
            <div>
              <label htmlFor="merchant" className="block text-sm font-medium text-gray-700">
                Select Merchant
              </label>
              <select
                id="merchant"
                value={selectedMerchant}
                onChange={(e) => setSelectedMerchant(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              >
                <option value="">Select a merchant...</option>
                {merchants.map((merchant) => (
                  <option key={merchant.id} value={merchant.id}>
                    {merchant.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Bank Mappings */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-4">Map Banks</h3>
              <div className="space-y-4">
                {Object.entries(bankMappings).map(([datasetBank, mappedBankId]) => (
                  <div key={datasetBank} className="flex items-center space-x-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {datasetBank}
                      </label>
                    </div>
                    <div className="flex-1">
                      <select
                        value={bankMap[datasetBank] || ''}
                        onChange={(e) => setBankMap(prev => ({
                          ...prev,
                          [datasetBank]: e.target.value
                        }))}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      >
                        <option value="">Map to bank...</option>
                        {banks.map((bank) => (
                          <option key={bank.id} value={bank.id}>
                            {bank.name} ({bank.alias})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Mapping'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}